// @flow

import React, { PureComponent, type Node } from 'react';
import type { ComponentType } from 'react';
import { connect } from 'react-redux';
import { Field } from 'redux-form';
import {
  getDate,
  getEmail,
  getNoSelection,
  getPhone,
  getRequired,
} from '../../../store/formSettings/selectors';
import {
  date,
  email,
  phone,
  required,
  selectLength,
} from '../../../helpers/formValidators';
import {
  DATE_VALIDATOR,
  EMAIL_VALIDATOR,
  PHONE_VALIDATOR,
  REQUIRED_VALIDATOR,
  SELECT_LENGTH_VALIDATOR,
} from './constants';
import type { Validators } from './constants';

type OwnProps = {|
  withValidator?: Validators | Array<Validators>,
  label?: string,
  type?: string,
  placeholder?: string,
  value?: string,
  children?: Node,
|};

type StateProps = {|
  dateError: string,
  emailError: string,
  phoneError: string,
  requiredError: string,
  selectLengthError: string,
|};

type FieldProps = {|
  name: string,
  component: ComponentType<*> | string,
|};

type StatelessProps = {|
  ...OwnProps,
  ...FieldProps,
|};

type Props = {|
  ...StatelessProps,
  ...StateProps,
|};

const determineValidators = (props: Props) => {
  const {
    dateError,
    emailError,
    phoneError,
    requiredError,
    selectLengthError,
    withValidator,
  } = props;
  const validatorMap = {
    [DATE_VALIDATOR]: date(dateError),
    [EMAIL_VALIDATOR]: email(emailError),
    [PHONE_VALIDATOR]: phone(phoneError),
    [REQUIRED_VALIDATOR]: required(requiredError),
    [SELECT_LENGTH_VALIDATOR]: selectLength(selectLengthError),
  };
  let validators;

  if (!withValidator) {
    return [];
  }

  // transform to array or clone array
  if (!Array.isArray(withValidator)) {
    validators = [withValidator];
  } else {
    validators = [...withValidator];
  }

  return validators.map<any>((validator: Validators) => validatorMap[validator]);
};

class ReduxFormField extends PureComponent<Props, *> {
  static defaultProps = {
    withValidator: [],
    label: '',
    type: '',
    placeholder: '',
    value: '',
    children: '',
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      validators: determineValidators(props),
    };
  }

  static getDerivedStateFromProps(newProps: Props, prevState: Props) {
    if (
      prevState.dateError !== newProps.dateError
      || prevState.emailError !== newProps.emailError
      || prevState.phoneError !== newProps.phoneError
      || prevState.requiredError !== newProps.requiredError
      || prevState.selectLengthError !== newProps.selectLengthError
      || prevState.withValidator !== newProps.withValidator
    ) {
      return {
        validators: determineValidators(newProps),
      };
    }

    return null;
  }

  render() {
    const { withValidator, ...otherProps } = this.props;
    const { validators } = this.state;

    return (
      <Field
        validate={validators}
        {...otherProps}
      />
    );
  }
}

const mapStateToProps = (state): StateProps => ({
  dateError: getDate(state),
  emailError: getEmail(state),
  phoneError: getPhone(state),
  requiredError: getRequired(state),
  selectLengthError: getNoSelection(state),
});

export default connect<any, StatelessProps, StateProps, any, any, any>(
  mapStateToProps,
)(ReduxFormField);
